// Golf 9 on Vandaag: the yearly dates of the day, "Hoe was je dag?" at Wind down, and the rest screen after bedtime.
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Gift, Moon } from "lucide-react";
import { dateLabel } from "@shared/dates";
import { addDays, afterBedtime, displayDate } from "@shared/time";
import { Button, cn } from "../components/ui";
import { getMeta, setMeta, type DayBlock } from "../lib/db";
import { setReflection, useDatesOn, useReflection } from "../lib/dates";
import { navigate } from "../lib/hooks";

/** "Mam jarig (60)" on the day itself: a card like an appointment, without a time. */
export function DatesOfDay({ date }: { date: string }) {
  const dates = useDatesOn(date);
  if (!dates?.length) return null;
  return (
    <ul aria-label="Vandaag bijzonder" className="mb-5 flex flex-col gap-2">
      {dates.map((d) => (
        <li key={d.id}>
          <a
            href="/meer/verjaardagen"
            onClick={(e) => {
              e.preventDefault();
              navigate("/meer/verjaardagen");
            }}
            className="flex min-h-14 items-center gap-3 rounded-2xl border-l-[6px] border-sun bg-sun/20 px-3 py-2"
          >
            <Gift aria-hidden className="size-5 shrink-0 text-ink" />
            <span className="min-w-0 flex-1">
              <span className="block text-lg font-semibold [overflow-wrap:anywhere]">{dateLabel(d, date)}</span>
              {d.ideas && <span className="block truncate text-sm text-ink-soft">Idee: {d.ideas.split("\n")[0]}</span>}
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

const MOODS = [
  { mood: 3, emoji: "🙂", label: "Goed" },
  { mood: 2, emoji: "😐", label: "Gewoon" },
  { mood: 1, emoji: "🙁", label: "Zwaar" },
] as const;

/** "Hoe was je dag?" under Wind down (golf 9): one tap, a sentence if you like; read by the Sunday look back. */
export function ReflectionCard({ date }: { date: string }) {
  const reflection = useReflection(date);
  const [note, setNote] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  if (reflection === undefined) return null;
  const chosen = reflection?.mood;
  if (reflection && !editing) {
    const m = MOODS.find((x) => x.mood === reflection.mood)!;
    return (
      <p className="mb-1 ml-10 flex flex-wrap items-center gap-x-2 text-sm text-ink-soft">
        <span>
          Je dag: <span aria-hidden>{m.emoji}</span> {m.label.toLowerCase()}
          {reflection.note ? `, "${reflection.note}"` : ""}
        </span>
        <button type="button" onClick={() => setEditing(true)} className="min-h-11 font-semibold text-pine underline underline-offset-4">
          Wijzigen
        </button>
      </p>
    );
  }
  const text = note ?? reflection?.note ?? "";
  return (
    <section aria-label="Hoe was je dag" className="mb-2 ml-10 rounded-xl border-2 border-mist bg-paper p-2">
      <p className="mb-2 font-semibold">Hoe was je dag?</p>
      <div role="radiogroup" aria-label="Hoe was je dag" className="mb-2 grid grid-cols-3 gap-2">
        {MOODS.map((m) => (
          <button
            key={m.mood}
            type="button"
            role="radio"
            aria-checked={chosen === m.mood}
            onClick={async () => {
              await setReflection(date, m.mood, text || null);
              setEditing(false);
            }}
            className={cn(
              "flex min-h-14 flex-col items-center justify-center rounded-xl border-2 text-sm",
              chosen === m.mood ? "border-pine bg-pine/15 font-semibold" : "border-mist",
            )}
          >
            <span aria-hidden className="text-2xl leading-none">
              {m.emoji}
            </span>
            {m.label}
          </button>
        ))}
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-ink-soft">Eén zin erbij, als je wilt</span>
        <input
          value={text}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (reflection && note !== null && note !== (reflection.note ?? "")) void setReflection(date, reflection.mood, note || null);
          }}
          className="min-h-11 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
        />
      </label>
    </section>
  );
}

/**
 * Wind down (B49): from the start of the fixed step Bedtijd until the day switches at 04:00, Vandaag rests.
 * Soft: "Toch openen" lets the day through for the rest of that night, on this device.
 */
export function useRestScreen(date: string, minute: number, blocks: DayBlock[] | null, on: boolean | undefined): boolean | undefined {
  const opened = useLiveQuery(async () => (await getMeta<string>("windDownOpened")) === date, [date]);
  const bed = blocks?.find((b) => b.slot_kind === "sleep" && b.is_fixed);
  // Before bedtime the answer is known without the setting: never hold the day back then (Welkom terug runs once).
  if (!bed || minute < 0 || !afterBedtime(minute, bed.start_local)) return false;
  if (on === undefined || opened === undefined) return undefined;
  return on && !opened;
}

export function RestScreen({ date, tomorrowReady }: { date: string; tomorrowReady: boolean }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-xl flex-col px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <section aria-label="Welterusten" className="flex flex-1 flex-col items-center justify-center text-center">
        <Moon aria-hidden className="mb-4 size-12 text-pine" />
        <h1 className="mb-2 text-[2.25rem] leading-tight font-bold">Welterusten</h1>
        <p className="mb-10 text-lg text-ink-soft">
          {tomorrowReady ? `Morgen staat klaar (${displayDate(addDays(date, 1))}).` : "Morgen is nog niet gepland. Dat komt morgen wel."}
        </p>
        <Button variant="quiet" onClick={() => void setMeta("windDownOpened", date)}>
          Toch openen
        </Button>
      </section>
    </main>
  );
}
